import { useState, useEffect } from "react";
import { Plus, Pencil, Trash2, X, Eye, EyeOff } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const EMPTY_BLOG = { title: "", slug: "", excerpt: "", content: "", author: "", category: "insight", published: false, meta_title: "", meta_description: "" };

export default function AdminBlogs() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editBlog, setEditBlog] = useState(null);
  const [form, setForm] = useState(EMPTY_BLOG);
  const [saving, setSaving] = useState(false);

  const fetchBlogs = async () => {
    try {
      const { data } = await axios.get(`${API}/api/blogs?published_only=false`);
      setBlogs(data);
    } catch { /* empty */ }
    setLoading(false);
  };

  useEffect(() => { fetchBlogs(); }, []);

  const openCreate = () => { setForm(EMPTY_BLOG); setEditBlog("new"); };
  const openEdit = (blog) => { setForm({ ...EMPTY_BLOG, ...blog }); setEditBlog(blog.id); };
  const close = () => { setEditBlog(null); setForm(EMPTY_BLOG); };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editBlog === "new") {
        await axios.post(`${API}/api/blogs`, form);
      } else {
        await axios.put(`${API}/api/blogs/${editBlog}`, form);
      }
      close();
      fetchBlogs();
    } catch { /* empty */ }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this blog post?")) return;
    await axios.delete(`${API}/api/blogs/${id}`);
    fetchBlogs();
  };

  const togglePublish = async (blog) => {
    await axios.put(`${API}/api/blogs/${blog.id}`, { published: !blog.published });
    fetchBlogs();
  };

  const autoSlug = (title) => title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

  return (
    <div data-testid="admin-blogs-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif text-2xl text-navy-900">Blog Posts</h1>
        <button onClick={openCreate} data-testid="create-blog-button" className="flex items-center gap-2 bg-navy-900 text-white px-5 py-2.5 rounded-sm text-sm font-sans font-medium hover:bg-navy-800 transition-colors">
          <Plus className="w-4 h-4" /> New Post
        </button>
      </div>

      {loading ? (
        <p className="text-gray-400 font-sans text-sm">Loading...</p>
      ) : blogs.length === 0 ? (
        <div className="text-center py-20 border border-gray-200 bg-white">
          <p className="text-gray-400 font-sans">No blog posts yet.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {blogs.map((blog) => (
            <div key={blog.id} data-testid={`blog-item-${blog.id}`} className="bg-white border border-gray-200 p-4 flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-sans text-sm font-medium text-navy-900">{blog.title}</h3>
                  <span className={`text-xs px-2 py-0.5 rounded-sm ${blog.published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {blog.published ? "Published" : "Draft"}
                  </span>
                </div>
                <p className="text-xs text-gray-400 font-sans mt-1">{blog.category} | {blog.author}</p>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => togglePublish(blog)} className="p-2 text-gray-400 hover:text-navy-900 transition-colors" title={blog.published ? "Unpublish" : "Publish"}>
                  {blog.published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                <button onClick={() => openEdit(blog)} data-testid={`edit-blog-${blog.id}`} className="p-2 text-gray-400 hover:text-navy-900 transition-colors">
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => handleDelete(blog.id)} data-testid={`delete-blog-${blog.id}`} className="p-2 text-gray-400 hover:text-red-500 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Dialog */}
      <Dialog open={editBlog !== null} onOpenChange={(open) => { if (!open) close(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-navy-900">
              {editBlog === "new" ? "Create Blog Post" : "Edit Blog Post"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-sm font-sans font-medium text-gray-700 mb-1 block">Title</Label>
              <Input
                data-testid="blog-title-input"
                value={form.title}
                onChange={(e) => { setForm({ ...form, title: e.target.value, slug: autoSlug(e.target.value) }); }}
                className="border-gray-200 rounded-sm h-10 font-sans"
              />
            </div>
            <div>
              <Label className="text-sm font-sans font-medium text-gray-700 mb-1 block">Slug</Label>
              <Input data-testid="blog-slug-input" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="border-gray-200 rounded-sm h-10 font-sans" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-sans font-medium text-gray-700 mb-1 block">Author</Label>
                <Input data-testid="blog-author-input" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} className="border-gray-200 rounded-sm h-10 font-sans" />
              </div>
              <div>
                <Label className="text-sm font-sans font-medium text-gray-700 mb-1 block">Category</Label>
                <Input data-testid="blog-category-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="border-gray-200 rounded-sm h-10 font-sans" />
              </div>
            </div>
            <div>
              <Label className="text-sm font-sans font-medium text-gray-700 mb-1 block">Excerpt</Label>
              <Textarea data-testid="blog-excerpt-input" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className="border-gray-200 rounded-sm font-sans min-h-[60px]" />
            </div>
            <div>
              <Label className="text-sm font-sans font-medium text-gray-700 mb-1 block">Content</Label>
              <Textarea data-testid="blog-content-input" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="border-gray-200 rounded-sm font-sans min-h-[180px]" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-sans font-medium text-gray-700 mb-1 block">Meta Title</Label>
                <Input value={form.meta_title} onChange={(e) => setForm({ ...form, meta_title: e.target.value })} className="border-gray-200 rounded-sm h-10 font-sans" />
              </div>
              <div>
                <Label className="text-sm font-sans font-medium text-gray-700 mb-1 block">Meta Description</Label>
                <Input value={form.meta_description} onChange={(e) => setForm({ ...form, meta_description: e.target.value })} className="border-gray-200 rounded-sm h-10 font-sans" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="published" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="rounded border-gray-300" />
              <Label htmlFor="published" className="text-sm font-sans text-gray-700">Published</Label>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <button onClick={close} className="px-5 py-2.5 border border-gray-200 text-gray-600 rounded-sm text-sm font-sans hover:bg-gray-50 transition-colors">Cancel</button>
              <button onClick={handleSave} data-testid="save-blog-button" disabled={saving} className="px-5 py-2.5 bg-navy-900 text-white rounded-sm text-sm font-sans font-medium hover:bg-navy-800 transition-colors disabled:opacity-50">
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
